<template>
  <div class="container" id="actionsOverview">
    <div class="row mt-2 header-row" id="actionsOverview-headerRowNav">
      <div class="col-auto" id="actionsOverview-title">
        <h5 class="mt-1 mb-0">{{ 'ACTIONS_OVERVIEW' | translate }}</h5>
      </div>
      <div class="col text-center" id="actionsOverview-switchDiv">
        <div class="btn-group custom-switches" role="group" id="actionsOverview-switch">
          <button type="button" class="btn btn-default" :class="{ 'active': 'all' === activeFilter }"
                  @click="setFilter('all')" id="actionsOverview-switchButton-All">{{ 'ALL' | translate }}</button>
          <button type="button" class="btn btn-default" :class="{ 'active': type === activeFilter }"
                  v-for="(actions, type) in actionTypes" @click="setFilter(type)" :id="'actionsOverview-switchButton-'+type">{{ type | translate('action') }}</button>
        </div>
      </div>
      <div class="col-auto" id="actionsOverview-ButtonDiv">
        <button type="button" class="btn btn-default text-danger" @click="showConfirmForCancelQueue = true"
                id="actionsOverview-cancelQueueButton">{{ 'CANCEL_QUEUED_ACTIONS' | translate }}</button>
      </div>
    </div>
    <div v-if="!error" class="mt-4" id="actionsOverview-rowTable">
      <table class="table main-table" id="actionsAccordion">
        <caption class="accessibly-hidden">{{ 'ACTIONS_OVERVIEW' | translate }}</caption>
        <thead id="actionsOverview-actionsAccordion-tableHead">
          <tr>
            <th scope="col" class="tcell-30"></th>
            <th scope="col">{{ 'DATE_OF_ACTION' | translate }}</th>
            <th scope="col">{{ 'BATCHES' | translate }}</th>
            <th scope="col">{{ 'STATUSES_PER_PRODUCT_LINE' | translate }}</th>
            <th scope="col">{{ 'AUTHOR' | translate }}</th>
            <th scope="col">{{ 'ACTION' | translate }}</th>
          </tr>
        </thead>
        <tbody v-if="actions.length > 0 && !loading" v-for="(action, i) in actionsWithGroupedActionDetails" :id="'actionsOverview-actionsAccordion-tableBody-'+action.startDate">
          <tr class="collapsed" aria-expanded="false" data-toggle="collapse" :data-target="'#collapse'+i" :id="'actionsOverview-actionsAccordion-tableRow-'+action.startDate">
            <td class="tcell-30" :id="'actionsOverview-actionsAccordion-tableData-'+action.startDate">
              <i class="fa fa-chevron-right text-info" aria-hidden="true" :id="'actionsOverview-actionsAccordion-tableData-arrow-'+action.startDate"></i>
              <i class="fa fa-chevron-down text-info" aria-hidden="true"></i>
            </td>
            <td>{{ action.startDate }}</td>
            <td class="font-weight-semibold">
              <template v-for="(batch, batchId) in action.jobs">
                <span v-if="batchId !== 'undefined'">{{ batchId }}</span>
                <br v-for="detail in batch" />
              </template>
            </td>
            <td>
              <template v-for="batch in action.jobs">
                <template v-for="detail in batch">
                  <span class="font-weight-semibold" v-if="detail.productLine">{{ detail.productLine | translate('product') }}</span>
                  <span v-if="detail.policyCasesStatus">- {{ detail.policyCasesStatus | translate('status') }}</span>
                  <br/>
                </template>
              </template>
            </td>
            <td>{{ action.author }}</td>
            <td :class="{ 'text-success': action.status === 'COMPLETE',
                          'text-info': action.status === 'RUNNING',
                          'text-primary': action.status === 'PENDING' || action.status === 'PAUSED',
                          'text-warning': action.status === 'CANCELED',
                          'text-danger': action.status === 'FAILED' }">
              <i v-if="action.status === 'COMPLETE'" :title="'COMPLETED' | translate" class="fa fa-check mr-1" aria-hidden="true"></i>
              <i v-else-if="action.status === 'RUNNING'" :title="'RUNNING' | translate('action')" class="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i>
              <i v-else-if="action.status === 'PENDING'" :title="'PENDING' | translate('job')" class="fa fa-clock-o mr-1" aria-hidden="true"></i>
              <i v-else-if="action.status === 'CANCELED'" :title="'CANCELED' | translate('job')" class="fa fa-user-times mr-1" aria-hidden="true"></i>
              <i v-else-if="action.status === 'PAUSED'" :title="'PAUSED' | translate('job')" class="fa fa-pause mr-1" aria-hidden="true"></i>
              <i v-else-if="action.status === 'FAILED'" :title="'FAILED' | translate('job')" class="fa fa-times mr-1" aria-hidden="true"></i>
              {{ action.type | translate('action') }}
              <i v-if="action.status === 'PENDING' || action.status === 'PAUSED'" :title="'CANCEL' | translate" v-on:click.stop="cancelAction(action.id)"
                 class="fa fa-times mt-1 text-danger hover-visible float-right fa-lg clickable" aria-hidden="true"></i>
            </td>
          </tr>
          <tr :id="'actionsOverview-actionsAccordion-innerTable-of-'+action.startDate">
            <td colspan="6" class="inner-table-container" :id="'actionsOverview-actionsAccordion-innerTableContainer-of-'+action.startDate">
              <div :id="'collapse'+i" class="collapse" data-parent="#actionsAccordion">
                <table class="table" id="actionsOverview-innerOverViewTable">
                  <caption class="accessibly-hidden">{{ 'ACTIONS_OVERVIEW' | translate }}</caption>
                  <thead :id="'actionsOverview-innerOverViewTable-head-'+action.startDate">
                    <tr :id="'actionsOverview-innerOverViewTable-row-'+action.startDate">
                      <th scope="col">{{ 'NAME' | translate }}</th>
                      <th scope="col" class="tcol-20">{{ 'BATCH' | translate }}</th>
                      <th scope="col" class="tcol-20">{{ 'PRODUCT_LINE' | translate }} - {{ 'STATUS' | translate }}</th>
                      <th scope="col" class="tcol-10">{{ 'TOTAL_ROWS' | translate }}</th>
                      <th scope="col" class="tcol-10">{{ 'STATUS' | translate }}</th>
                      <th scope="col" class="tcell-100"></th>
                    </tr>
                  </thead>
                  <tbody :id="'actionsOverview-innerOverViewTable-body-'+action.startDate">
                    <template v-for="batch in action.jobs">
                      <tr v-for="job in batch" :id="'actionsOverview-innerOverViewTable-bodyRow-'+job.productLineName">
                        <td class="text-truncate" :id="'actionsOverview-innerOverViewTable-bodyData-'+job.productLineName">
                          <router-link v-bind:to="'/job/' + job.id" :id="'actionsOverview-innerOverViewTable-bodyRow-link-'+job.name">{{ job.name }}</router-link></td>
                        <td>{{ job.batchId }}</td>
                        <td>
                          <span v-if="job.productLineName">- {{ getDescriptionByName(job.productLineName) }}</span>
                          <span v-if="job.policyCasesStatus">- {{ job.policyCasesStatus | translate('status') }}</span>
                        </td>
                        <td>
                          <!--AA-709 The "total antal" for CLOSE DELETE and EXPORT should be based on the job status - 0 or totalRows
                          (they do not use completedRows in the same way as IMPORT and RECALCULATE). This should be a temporary solution. -->
                          <template v-if="action.type === 'CLOSE' || action.type === 'DELETE_CASES' || action.type === 'EXPORT' || action.type ==='DELETE_CUSTOMERS'">
                            {{ job.status === "COMPLETE" ? job.totalRows : 0 }}
                          </template>
                          <template v-else>
                            {{ job.completedRows - job.errorCount }}
                          </template>
                          / {{ job.totalRows }}
                        </td>
                        <td>
                          <span class="badge text-uppercase" :class="statusBadgeClass(job.status).badge">
                            <i class="fa mr-1" :class="statusBadgeClass(job.status).icon" v-if="statusBadgeClass(job.status).icon" aria-hidden="true"></i>
                            {{ job.status | translate('job') }}
                          </span>
                        </td>
                        <td class="text-right" :id="'actionsOverview-innerOverViewTable-bodyRow-performButton-'+job.name">
                          <template v-if="job.status !== 'CANCELED' && job.status !== 'COMPLETE' && job.status !== 'FAILED'">
                            <a href="#" @click.prevent="pauseJob(job.id)"
                               v-if="job.status === 'RUNNING' || job.status === 'PENDING'"
                               :title="'PAUSE' | translate" class="ml-2" :id="'actionsOverview-innerOverViewTable-pauseButton-'+job.name">
                              <i class="fa fa-pause icon-size-1rem" aria-hidden="true" :id="'actionsOverview-innerOverViewTable-pauseIcon-'+job.name"></i>
                            </a>
                            <a href="#" @click.prevent="startJob(job.id)"
                               v-if="job.status !== 'RUNNING' && job.status !== 'PENDING'"
                               :title="'START' | translate" class="ml-2" :id="'actionsOverview-innerOverViewTable-startButton-'+job.name">
                              <i class="fa fa-play icon-size-1rem" aria-hidden="true" :id="'actionsOverview-innerOverViewTable-startIcon-'+job.name"></i>
                            </a>
                            <a href="#" @click.prevent="confirmCancelJob(job.id)"
                               v-if="job.status === 'PAUSED' && (action.type.startsWith('IMPORT') || action.type === 'RECALC')"
                               :title="'CANCEL' | translate" class="ml-2" :id="'actionsOverview-innerOverViewTable-cancelButton-'+job.name">
                              <i class="fa fa-times icon-size-1rem text-danger" aria-hidden="true" :id="'actionsOverview-innerOverViewTable-cancelIcon-'+job.name"></i>
                            </a>
                          </template>
                          <template v-if="action.type === 'EXPORT' && action.status === 'COMPLETE'">
                            <template v-if="job.exportFileExists">
                              <a href="#" @click.prevent="download(job.name)"
                              :title="'DOWNLOAD_FILE' | translate" class="ml-2" :id="'actionsOverview-innerOverViewTable-downloadButton-'+job.name">
                                <i class="fa fa-download icon-size-1rem" aria-hidden="true" :id="'actionsOverview-innerOverViewTable-downloadIcon-'+job.name"></i>
                              </a>
                              <a href="#" @click.prevent="selectExportForDelete(job.name, i)" class="ml-2"
                              :title="'DELETE_FILE' | translate" :id="'actionsOverview-innerOverViewTable-deleteButton-'+job.name">
                                <i class="fa fa-trash-o text-danger icon-size-1rem" aria-hidden="true" :id="'actionsOverview-innerOverViewTable-deleteIcon-'+job.name"></i>
                              </a>
                            </template>
                            <template v-else>
                              <span :title="'MISSING_EXPORT_FILE' | translate('msg')" class="fa-stack ml-2" :id="'actionsOverview-MISSING_EXPORT_FILE'+job.name">
                                <i class="fa fa-file-o fa-stack-1x text-warning" aria-hidden="true" :id="'actionsOverview-MISSING_EXPORT_FILE-warning'+job.name"></i>
                                <i class="fa fa-trash-o fa-stack-1x text-danger icon-bottom-right" aria-hidden="true" :id="'actionsOverview-MISSING_EXPORT_FILE-icon'+job.name"></i>
                              </span>
                            </template>
                          </template>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="alert alert-danger mt-4">{{ error | translate('msg') }}</div>
    <div v-if="actions.length <= 0 && !loading" class="text-center mt-4">{{ 'LIST_ACTIONS_EMPTY' | translate('msg') }}</div>
    <div class="cm-loader" v-if="loading">
      <custom-spinner></custom-spinner>
    </div>

    <transition name="fade" mode="out-in">
      <pagination-list
        v-model="currentPage"
        v-bind:currentPage="currentPage"
        v-bind:totalCount="totalCount"
        v-bind:pageSize="pageSize"
        id="actionsOverview-paginationList"
      ></pagination-list>
    </transition>

    <confirm-dialog :show="showConfirmForCancelQueue"
                    :textLabel="'msg.SURE_CANCEL_PENDING_ACTIONS'"
                    @hide-dialog="showConfirmForCancelQueue = false"
                    @confirm="cancelQueue"
                    id="actionsOverview-SURE_CANCEL_PENDING_ACTIONS">
    </confirm-dialog>
    <confirm-dialog :show="showConfirmForCancelAction"
                    :textLabel="'msg.SURE_CANCEL_ACTION'"
                    @hide-dialog="showConfirmForCancelAction = false"
                    @confirm="cancelJob"
                    id="actionsOverview-SURE_CANCEL_ACTION">
    </confirm-dialog>
    <confirm-dialog :show="showConfirmForDeleteExport"
                    :textLabel="'msg.SURE_DELETE_FILE'"
                    @hide-dialog="showConfirmForDeleteExport = false"
                    @confirm="deleteFile"
                    id="actionsOverview-SURE_DELETE_FILE">
    </confirm-dialog>
  </div>
</template>

<script>
import RestService from '../../service/RestService.js';
import PaginationList from '../search/PaginationList';
import ConfirmDialog from '../dialog/ConfirmDialog';
import CustomSpinner from '../CustomSpinner.vue';
import GeneralConfigService from '../../service/GeneralConfigService';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const DEFAULT_FILTER_TYPE = 'all';
export default {
  components: { PaginationList, ConfirmDialog, CustomSpinner },
  props: {
    type: { default: DEFAULT_FILTER_TYPE },
    page: { default: DEFAULT_PAGE },
    size: { default: DEFAULT_PAGE_SIZE }
  },
  data: function() {
    return {
      rest: this.initRestService(),
      actions: [],
      totalCount: null,
      loading: false,
      error: null,
      actionTypes: {
        IMPORT_CUSTOMERS: [ 'IMPORT_CUSTOMERS' ],
        IMPORT_POLICIES: [ 'IMPORT_POLICIES' ],
        IMPORT_COVERS: [ 'IMPORT_COVERS' ],
        IMPORT_NVGA2: ['IMPORT_NVGA2'],
        EXPORT: [ 'EXPORT' ],
        RECALC: [ 'RECALC' ],
        CLOSE: [ 'CLOSE' ],
        DELETE: [ 'DELETE_CASES', 'DELETE_CUSTOMERS', 'DELETE_CASE_IDS' ]
      },
      activeFilter: this.type,
      currentPage: this.page,
      pageSize: this.size,
      showConfirmForCancelQueue: false,
      showConfirmForCancelAction: false,
      showConfirmForDeleteExport: false,
      jobToCancelId: null,
      lastExportForDelete: {
        fileName: null,
        actionIndex: null
      },
      pollingInterval: null,
      POLLING_PERIOD_MS: 2000,
      componentActive: true,
      productLines: []
    }
  },
  beforeMount() {
    window.addEventListener('focus', this.setPollingInterval);
    window.addEventListener('blur', this.clearPollingInterval);
  },
  async created() {
    await this.getProductlines();
  },
  mounted() {
    this.init();
    this.setPollingInterval();
  },
  beforeRouteLeave(to, from, next) {
    this.componentActive = false;
    this.clearPollingInterval();
    window.removeEventListener('focus', this.setPollingInterval);
    window.removeEventListener('blur', this.clearPollingInterval);
    next();
  },
  beforeDestroy() {
    this.componentActive = false;
    this.clearPollingInterval();
  },
  computed: {
    actionsWithGroupedActionDetails: function() {
      let groups = [];
      if (this.actions.length > 0) {
        this.actions.forEach((action, index) => {
          groups[index] = Object.assign({}, action);
          groups[index]['jobs'] = {};
          const jobs = Array.isArray(action.jobs) ? action.jobs : []; // <-- guard
          jobs.forEach((detail) => {
            if (!groups[index]['jobs'][detail && detail.batchId]) {
              groups[index]['jobs'][detail && detail.batchId] = [];
            }
            groups[index]['jobs'][detail && detail.batchId].push(detail || {});
            groups[index]['jobs'][detail && detail.batchId] = groups[index]['jobs'][detail && detail.batchId].sort(this.compareJobs);
          });
        });
      }
      return groups;
    }
  },
  methods: {
    initRestService() {
      return new RestService();
    },
    async init() {
      if (!this.componentActive) return;
      this.loading = true;
      this.makeCorrectionOnPaginationValues();
      this.countActions();
      await this.loadActions();
      this.loading = false;
    },
    refresh() {
      if (!this.componentActive) return;
      this.makeCorrectionOnPaginationValues();
      this.loadActions();
      this.countActions();
    },
    makeCorrectionOnPaginationValues: function () {
      if (this.pageSize > 1000 || this.pageSize < 1) {
        this.pageSize = DEFAULT_PAGE_SIZE;
      }
      if (this.currentPage < 1) {
        this.currentPage = DEFAULT_PAGE;
      }
    },
    loadActions: async function() {
      if (!this.componentActive) return;
      this.error = null;
      try {
        let types = (this.activeFilter !== DEFAULT_FILTER_TYPE) ? this.actionTypes[this.activeFilter] : null;
        let res = await this.rest.getBatchActions(types, this.currentPage, this.pageSize);
        this.actions = res.data;
      } catch (err) {
        this.error = 'SOMETHING_WRONG_FETCH_ACTIONS';
      }
    },
    countActions: async function() {
      if (!this.componentActive) return;
      let types = (this.activeFilter !== DEFAULT_FILTER_TYPE) ? this.actionTypes[this.activeFilter] : null;
      let res = await this.rest.countBatchActions(types);
      this.totalCount = res.data;
    },
    setFilter(type) {
      this.activeFilter = type;
    },
    updateUrl() {
      let url = '/action?page=' + this.currentPage;
      if (this.activeFilter !== DEFAULT_FILTER_TYPE) {
        url += '&type=' + this.activeFilter;
      }
      if (this.pageSize !== DEFAULT_PAGE_SIZE) {
        url += '&size=' + this.pageSize;
      }
      this.$router.replace(url);
    },
    cancelQueue: async function() {
      this.showConfirmForCancelQueue = false;
      await this.rest.cancelJobQueue();
      this.init();
    },
    cancelAction: async function (actionId) {
      await this.rest.cancelAction(actionId);
      this.init();
    },
    statusBadgeClass(jobStatus) {
      switch (jobStatus) {
        case 'COMPLETE':
          return { badge: 'badge-success', icon: 'fa-check' };
        case 'RUNNING':
          return { badge: 'badge-info', icon: 'fa-refresh fa-spin' };
        case 'PAUSED':
          return { badge: 'badge-primary', icon: 'fa-pause' };
        case 'PENDING':
          return { badge: 'badge-primary', icon: 'fa-clock-o' };
        case 'CANCELED':
          return { badge: 'badge-warning', icon: 'fa-user-times' };
        case 'EXPIRED':
          return { badge: 'badge-warning', icon: 'fa-clock-o' };
        case 'FAILED':
        case 'VALIDATION_FAILED':
          return { badge: 'badge-danger', icon: 'fa-times' };
        default:
          return { badge: 'badge-secondary', icon: null };
      }
    },
    download(exportFileName) {
      this.rest.downloadExportFile(exportFileName).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', exportFileName);
        link.click();
        window.URL.revokeObjectURL(url);
      });
    },
    selectExportForDelete(exportFileName, actionIndex) {
      this.lastExportForDelete.fileName = exportFileName;
      this.lastExportForDelete.actionIndex = actionIndex;
      this.showConfirmForDeleteExport = true;
    },
    deleteFile() {
      let exportFileName = this.lastExportForDelete.fileName;
      let actionIndex = this.lastExportForDelete.actionIndex;
      this.showConfirmForDeleteExport = false;
      this.rest.deleteExportOutput(exportFileName).then((result) => {
        setTimeout(() => {
          this.$notify({
            type: 'alert-success',
            text: 'Bestand ' + exportFileName + ' is succesvol verwijderd.'
          });
        }, 300);
        this.actions[actionIndex].jobs[0].exportFileExists = false;
      }, (error) => {
        this.$notify({
          type: 'alert-danger',
          duration: -1,
          text: 'Opgeslagen om ' + exportFileName + ' te verwijderen. \n' + error.response ? error.response.data : ''
        });
      });
    },
    startJob(jobId) {
      this.rest.changeJobStatus(jobId, 'PENDING')
        .catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_STARTING_JOB', 'msg')
          });
          this.$log.error(err);
        });
    },
    pauseJob(jobId) {
      this.rest.changeJobStatus(jobId, 'PAUSED')
        .catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_PAUSING_JOB', 'msg')
          });
          this.$log.error(err);
        });
    },
    confirmCancelJob(jobId) {
      this.jobToCancelId = jobId;
      this.showConfirmForCancelAction = true;
    },
    cancelJob() {
      this.showConfirmForCancelAction = false;
      this.rest.cancelJob(this.jobToCancelId)
        .catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_CANCELING_JOB', 'msg')
          });
          this.$log.error(err);
        });
    },
    setPollingInterval() {
      this.pollingInterval = setInterval(this.refresh, this.POLLING_PERIOD_MS);
    },
    clearPollingInterval() {
      clearInterval(this.pollingInterval);
    },
    compareJobs: function (jobA, jobB) {
      // minimal, null-safe comparator
      const s = v => (v == null ? '' : String(v));
      const byName = s(jobA && jobA.name).localeCompare(s(jobB && jobB.name));
      if (byName !== 0) return byName;
      const byPL = s(jobA && jobA.productLineName).localeCompare(s(jobB && jobB.productLineName));
      if (byPL !== 0) return byPL;
      return s(jobA && jobA.policyCasesStatus).localeCompare(s(jobB && jobB.policyCasesStatus));
    },
    async getProductlines() {
      try {
        let response = await GeneralConfigService.getAllProductLines();
        this.productLines = response;
      } catch (error) {
        this.loadError = 'ERROR_LOADING_INSURERS';
        this.$log.error(error.response.data);
      }
    },
    getDescriptionByName(name) {
      for (let item of this.productLines) {
        if (item.name === name) {
          return item.description;
        }
      }
      return '-';
    }
  },
  watch: {
    activeFilter() {
      if (this.currentPage === DEFAULT_PAGE) {
        this.init();
        this.updateUrl();
      } else {
        this.currentPage = DEFAULT_PAGE;
      }
    },
    currentPage() {
      this.init();
      this.updateUrl();
    },
    totalCount() {
      if (this.currentPage > Math.ceil(this.totalCount / this.pageSize)) {
        this.currentPage = Math.ceil(this.totalCount / this.pageSize);
      }
    }
  }
}
</script>

<style scoped>
  .header-row .btn {
    font-size: .9rem;
  }
  table {
    table-layout: fixed;
  }
  .tcell-30 {
    width: 30px;
  }
  .tcell-100 {
    width: 100px;
  }
  /* MAIN TABLE */
  .main-table > tbody:nth-of-type(odd) > tr:first-child {
    background-color: #d1ecf1;
    background-color: rgba(0, 0, 0, .05);
  }
  td:first-child .fa-chevron-right,
  .collapsed td:first-child .fa-chevron-down {
    display: none;
  }
  .collapsed td:first-child .fa-chevron-right {
    display: inline;
  }
  /* INNER TABLE */
  .inner-table-container {
    padding: 0;
    border: 0;
  }
  .inner-table-container table {
    margin: 0;
  }
  /* COLLAPSE STYLES */
  .main-table > thead > tr > th {
    border-bottom: 0;
  }
  tr[data-toggle="collapse"] {
    border-right: 2px solid #17a2b8;
    border-left: 2px solid #17a2b8;
  }
  tr[data-toggle="collapse"] > td {
    border-top: 2px solid #17a2b8;
  }
  .inner-table-container > div {
    margin: 0 -1px;
    border: 2px solid #17a2b8;
    border-top: 0;
  }
  tr[data-toggle="collapse"]:not(.collapsed) {
    background-color: #d1ecf1 !important;
  }
  tr[data-toggle="collapse"].collapsed,
  tr[data-toggle="collapse"].collapsed > td,
  tr[data-toggle="collapse"].collapsed + tr > .inner-table-container > div {
    border-color: transparent;
  }
  .icon-size-1rem {
    font-size: 1rem;
  }
  .icon-bottom-right {
    right: -5px;
    bottom: -7px;
    left: auto;
  }
  .hover-visible {
    display: none;
  }
  tbody tr:hover .hover-visible,
  tbody tr.selected .hover-visible {
    display: inline;
  }

  tr[data-toggle="collapse"]:not(.collapsed) .hover-visible {
    display: inline;
  }
  .clickable{
    cursor: pointer;
  }

</style>



// WEBPACK FOOTER //
// src/components/action/ActionsOverview.vue